import { ButtonWithLoader } from "@components/button_with_loader/button_with_loader";
import { SelectField, SelectFieldOption } from "@components/fields";
import { FlashMessage } from "@components/flash_messages/flash_message";
import { Loader } from "@components/loader/loader";
import { useFetchArtists } from "@hooks/use_fetch_artists";
import { useMutate } from "@hooks/use_mutate";
import { api } from "@services/api";
import { isEmpty } from "@services/helpers";
import { isNil } from "@services/typed_helpers";
import { Form, Formik } from "formik";
import React, { FC, useMemo } from "react";

import * as types from "../../../types";

const RefreshArtistForm: FC = () => {
  const { artists, loading: loadingArtists } = useFetchArtists();
  const refreshArtist = useMemo(() => {
    return (token: string) =>
      ({ artistId }: types.RefreshArtistRequest) =>
        api.cache.refreshArtist(token, artistId);
  }, []);

  const {
    submitData: submit,
    loading: submitting,
    error: apiError,
    data: refreshResult,
  } = useMutate<types.RefreshArtistResponse, types.RefreshArtistRequest>(
    refreshArtist,
  );
  const { errors } = refreshResult ?? {};
  const handleSubmit = (values: { artistId: number | string }) => {
    if (loadingArtists || submitting) {
      return;
    }
    submit(values);
  };

  const options = useMemo(
    () =>
      (artists || [])
        .map((artist) => ({
          value: artist.mauArtistId,
          name: artist.fullName,
        }))
        .filter(({ name }) => !isNil(name)) as SelectFieldOption[],
    [artists],
  );

  if (loadingArtists) {
    return <Loader />;
  }
  if (isEmpty(artists)) {
    return <article>We didn't find any artists in the system.</article>;
  }

  return (
    <>
      {errors ? <FlashMessage message={errors} type="error" /> : null}
      {apiError ? (
        <FlashMessage
          message="Something terrible has happened.  Please try again later."
          type="error"
        />
      ) : null}
      {!isNil(refreshResult?.message) ? (
        <FlashMessage message={refreshResult.message} type="notice" />
      ) : null}
      <Formik
        initialValues={{ artistId: (artists || [])[0]?.mauArtistId }}
        onSubmit={handleSubmit}
        id="refresh_artist_form"
      >
        <Form>
          <SelectField options={options} label="Artist" name="artistId" />

          <ButtonWithLoader
            secondary
            loading={submitting}
            value="Refresh Artist!"
          />
        </Form>
      </Formik>
    </>
  );
};

export { RefreshArtistForm };
